import { useState } from 'react';
import { usePOS } from '@/contexts/POSContext';
import { Product } from '@/lib/posStorage';
import { getCurrencySymbol, formatFiat, fiatToBCH, formatBCH, calcTax } from '@/lib/bch';
import { useBCHPrice } from '@/hooks/useBCHPrice';
import { cn } from '@/lib/utils';

// ─── Product tile ─────────────────────────────────────────────────────────────

function ProductTile({ product, qty, onAdd, onRemove, currency }: {
  product: Product;
  qty: number;
  onAdd: () => void;
  onRemove: () => void;
  currency: string;
}) {
  const sym = getCurrencySymbol(currency);

  return (
    <div
      className={cn(
        'relative flex flex-col rounded-2xl overflow-hidden border transition-all select-none',
        qty > 0
          ? 'border-[#0AC18E] shadow-md shadow-[#0AC18E]/10 bg-white dark:bg-zinc-800'
          : 'border-zinc-100 dark:border-zinc-700 bg-white dark:bg-zinc-800'
      )}
    >
      {/* Image / icon area */}
      <button
        onPointerDown={(e) => { e.preventDefault(); onAdd(); }}
        className="relative w-full aspect-square bg-zinc-50 dark:bg-zinc-700 flex items-center justify-center overflow-hidden active:brightness-90 transition"
        aria-label={`Add ${product.name}`}
      >
        {product.imageDataUrl ? (
          <img src={product.imageDataUrl} alt={product.name} className="w-full h-full object-cover" />
        ) : (
          <span className="text-4xl">🛍️</span>
        )}
        {/* Qty badge */}
        {qty > 0 && (
          <span className="absolute top-2 right-2 w-6 h-6 rounded-full bg-[#0AC18E] text-white text-xs font-black flex items-center justify-center shadow">
            {qty}
          </span>
        )}
      </button>

      {/* Info */}
      <div className="px-2.5 pt-2 pb-2.5 flex flex-col gap-0.5">
        <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-50 leading-tight line-clamp-2">
          {product.name}
        </p>
        <p className="text-sm font-bold text-[#0AC18E]">
          {sym}{product.price.toFixed(2)}
        </p>

        {/* Qty controls — only when item is in cart */}
        {qty > 0 && (
          <div className="flex items-center gap-1 mt-1.5">
            <button
              onPointerDown={(e) => { e.preventDefault(); onRemove(); }}
              className="w-7 h-7 rounded-lg bg-zinc-100 dark:bg-zinc-700 text-zinc-600 dark:text-zinc-300 font-bold text-lg flex items-center justify-center active:scale-90 transition"
              aria-label="Remove one"
            >
              −
            </button>
            <span className="flex-1 text-center text-sm font-semibold text-zinc-800 dark:text-zinc-200">
              {qty}
            </span>
            <button
              onPointerDown={(e) => { e.preventDefault(); onAdd(); }}
              className="w-7 h-7 rounded-lg bg-[#0AC18E]/15 dark:bg-[#0AC18E]/25 text-[#0AC18E] font-bold text-lg flex items-center justify-center active:scale-90 transition"
              aria-label="Add one"
            >
              ＋
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Cart drawer ──────────────────────────────────────────────────────────────

function CartDrawer({ onCharge, onClose }: { onCharge: () => void; onClose: () => void }) {
  const { cart, cartTotal, updateCartQty, removeFromCart, clearCart, settings } = usePOS();
  const { data: priceData } = useBCHPrice(settings.currency);
  const sym = getCurrencySymbol(settings.currency);
  const { taxAmount, total: grandTotal } = calcTax(cartTotal, settings.taxRate);
  const bchTotal = priceData ? fiatToBCH(grandTotal, priceData.price) : 0;

  return (
    <div className="fixed inset-0 z-40 flex items-end justify-center bg-black/40 backdrop-blur-sm">
      <div className="w-full max-w-lg bg-white dark:bg-zinc-900 rounded-t-3xl pb-safe overflow-hidden flex flex-col max-h-[85dvh]">
        {/* Handle */}
        <div className="w-10 h-1 bg-zinc-200 dark:bg-zinc-700 rounded-full mx-auto mt-3 mb-1 shrink-0" />

        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 shrink-0">
          <h3 className="text-lg font-bold text-zinc-900 dark:text-zinc-50">Order Summary</h3>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 text-sm font-medium">
            Close
          </button>
        </div>

        {/* Items */}
        <div className="flex-1 overflow-y-auto px-5 space-y-3 py-2">
          {cart.map((item) => (
            <div key={item.product.id} className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-zinc-100 dark:bg-zinc-800 shrink-0 overflow-hidden flex items-center justify-center">
                {item.product.imageDataUrl ? (
                  <img src={item.product.imageDataUrl} alt={item.product.name} className="w-full h-full object-cover" />
                ) : (
                  <span className="text-xl">🛍️</span>
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-semibold text-zinc-900 dark:text-zinc-100 truncate">{item.product.name}</p>
                <p className="text-xs text-zinc-400">{sym}{item.product.price.toFixed(2)} each</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => updateCartQty(item.product.id, item.qty - 1)}
                  className="w-8 h-8 rounded-lg bg-zinc-100 dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 font-bold text-lg flex items-center justify-center"
                >
                  −
                </button>
                <span className="w-6 text-center text-sm font-semibold text-zinc-800 dark:text-zinc-200">
                  {item.qty}
                </span>
                <button
                  onClick={() => updateCartQty(item.product.id, item.qty + 1)}
                  className="w-8 h-8 rounded-lg bg-[#0AC18E]/15 text-[#0AC18E] font-bold text-lg flex items-center justify-center"
                >
                  ＋
                </button>
                <button
                  onClick={() => removeFromCart(item.product.id)}
                  className="w-8 h-8 rounded-lg bg-red-50 dark:bg-red-900/20 text-red-400 text-base flex items-center justify-center"
                  aria-label="Remove"
                >
                  ✕
                </button>
              </div>
              <div className="w-16 text-right shrink-0">
                <p className="text-sm font-bold text-zinc-900 dark:text-zinc-100">
                  {sym}{(item.product.price * item.qty).toFixed(2)}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="px-5 pt-4 pb-3 border-t border-zinc-100 dark:border-zinc-800 shrink-0 space-y-3">
          {/* Tax breakdown */}
          {settings.taxRate > 0 && (
            <div className="space-y-1 text-sm">
              <div className="flex justify-between text-zinc-500 dark:text-zinc-400">
                <span>Subtotal</span>
            <span>{sym}{cartGrandTotal.toFixed(2)}</span>
              </div>
              <div className="flex justify-between text-zinc-500 dark:text-zinc-400">
                <span>{settings.taxName} ({settings.taxRate}%)</span>
                <span>{sym}{taxAmount.toFixed(2)}</span>
              </div>
            </div>
          )}
          <div className="flex items-center justify-between">
            <span className="text-base font-semibold text-zinc-600 dark:text-zinc-300">Total</span>
            <div className="text-right">
              <p className="text-2xl font-black text-zinc-900 dark:text-zinc-50">
                {sym}{grandTotal.toFixed(2)}
              </p>
              {bchTotal > 0 && (
                <p className="text-sm font-mono text-[#0AC18E]">≈ {formatBCH(bchTotal)} BCH</p>
              )}
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => { clearCart(); onClose(); }}
              className="flex-1 py-4 rounded-2xl text-sm font-semibold bg-zinc-100 dark:bg-zinc-800 text-zinc-500 border border-zinc-200 dark:border-zinc-700"
            >
              Clear Cart
            </button>
            <button
              onClick={onCharge}
              className="flex-[2] py-4 rounded-2xl text-base font-bold bg-[#0AC18E] text-white shadow-lg shadow-[#0AC18E]/30 hover:bg-[#09AF81] active:scale-98 transition-all"
            >
              Charge {sym}{grandTotal.toFixed(2)}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── Main MenuOrderView ───────────────────────────────────────────────────────

export function MenuOrderView() {
  const { products, cart, cartTotal, addToCart, updateCartQty, settings, setView, setFiatAmount, setPendingBCH, setTipAmount, setDiscount } = usePOS();
  const { data: priceData } = useBCHPrice(settings.currency);
  const [activeCategory, setActiveCategory] = useState<string>('All');
  const [showCart, setShowCart] = useState(false);

  const sym = getCurrencySymbol(settings.currency);
  const available = products.filter((p) => p.available);
  const categories = ['All', ...Array.from(new Set(available.map((p) => p.category))).sort()];

  const displayed = activeCategory === 'All'
    ? available
    : available.filter((p) => p.category === activeCategory);

  const cartCount = cart.reduce((sum, i) => sum + i.qty, 0);
  const { total: cartGrandTotal } = calcTax(cartTotal, settings.taxRate);
  const bchCartTotal = priceData ? fiatToBCH(cartGrandTotal, priceData.price) : 0;

  const getQty = (id: string) => cart.find((i) => i.product.id === id)?.qty ?? 0;

  const handleCharge = () => {
    if (cartTotal <= 0) return;
    if (!settings.bchAddress) { setView('settings'); return; }
    const { total: grandTotal } = calcTax(cartTotal, settings.taxRate);
    const bch = priceData ? fiatToBCH(grandTotal, priceData.price) : 0;
    setTipAmount(0);
    setDiscount(0, '');
    setFiatAmount(cartTotal);
    setPendingBCH(bch);
    setShowCart(false);
    setView('discount');
  };

  return (
    <div className="flex flex-col h-full gap-3">
      {/* Category tabs */}
      {categories.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1 shrink-0 scrollbar-none">
          {categories.map((cat) => (
            <button
              key={cat}
              onClick={() => setActiveCategory(cat)}
              className={cn(
                'shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-all',
                activeCategory === cat
                  ? 'bg-[#0AC18E] text-white border-[#0AC18E] shadow-sm'
                  : 'bg-white dark:bg-zinc-800 text-zinc-500 dark:text-zinc-400 border-zinc-200 dark:border-zinc-700'
              )}
            >
              {cat}
            </button>
          ))}
        </div>
      )}

      {/* Empty state */}
      {available.length === 0 && (
        <div className="flex flex-col items-center justify-center flex-1 text-center border border-dashed border-zinc-200 dark:border-zinc-700 rounded-3xl py-12">
          <div className="text-5xl mb-4">🛍️</div>
          <p className="font-semibold text-zinc-700 dark:text-zinc-200 mb-1">No products in menu</p>
          <p className="text-sm text-zinc-400 mb-5">Go to Menu → Manage to add your products.</p>
          <button
            onClick={() => setView('menu-manage')}
            className="px-5 py-2.5 rounded-xl bg-[#0AC18E] text-white text-sm font-bold"
          >
            Manage Products
          </button>
        </div>
      )}

      {/* Product grid */}
      {displayed.length > 0 && (
        <div className="flex-1 overflow-y-auto pb-24">
          <div className="grid grid-cols-2 gap-3">
            {displayed.map((p) => (
              <ProductTile
                key={p.id}
                product={p}
                qty={getQty(p.id)}
                onAdd={() => addToCart(p)}
                onRemove={() => updateCartQty(p.id, getQty(p.id) - 1)}
                currency={settings.currency}
              />
            ))}
          </div>
        </div>
      )}

      {/* Sticky cart bar */}
      {cartCount > 0 && (
        <div className="fixed bottom-16 left-0 right-0 px-4 z-30">
          <button
            onClick={() => setShowCart(true)}
            className="w-full max-w-lg mx-auto flex items-center justify-between py-4 px-5 rounded-2xl bg-[#0AC18E] text-white shadow-xl shadow-[#0AC18E]/40 font-bold text-base active:scale-98 transition-all"
          >
            <span className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-white/25 text-white text-xs font-black flex items-center justify-center">
                {cartCount}
              </span>
              View Order
            </span>
            <span className="flex flex-col items-end">
              <span>{sym}{cartGrandTotal.toFixed(2)}</span>
              {bchCartTotal > 0 && (
                <span className="text-xs font-mono text-white/70 font-normal">≈ {formatBCH(bchCartTotal)} BCH</span>
              )}
            </span>
          </button>
        </div>
      )}

      {/* Cart drawer */}
      {showCart && (
        <CartDrawer
          onCharge={handleCharge}
          onClose={() => setShowCart(false)}
        />
      )}
    </div>
  );
}
